import {useRouter} from 'next/router';

export const MOBILE_APP_DISPLAY_TYPE_QUERY_PARAM = 'display_type';
export const MOBILE_APP_DISPLAY_TYPE_VALUE = 'mobile_app';

export interface IsMobileAppView {
    /** `true` once `router.query` is populated and safe to read. */
    isReady: boolean;
    /** `true` when the page is rendering inside the native app's webview. Always `false` until `isReady`. */
    isMobileApp: boolean;
}

/**
 * `router.query` values can be a single string or a string array (e.g. a duplicated query
 * param, `?display_type=mobile_app&display_type=web`). Take the last occurrence, matching how
 * Next.js itself resolves a duplicated param when read as a plain string on the server.
 */
function normalizeDisplayTypeQueryParam(raw: string | string[] | undefined): string | undefined {
    return Array.isArray(raw) ? raw[raw.length - 1] : raw;
}

/**
 * Detects whether the current page is being rendered inside the native mobile app's webview, via the
 * `display_type=mobile_app` query param the app appends when it opens a page.
 *
 * `router.query` is only populated once `router.isReady` is `true` (Next.js), so callers must not use
 * `isMobileApp` to gate a decision until `isReady` is also `true` — otherwise restricted content can
 * flash in before the query param resolves.
 */
export function useIsMobileAppView(): IsMobileAppView {
    const router = useRouter();
    const isReady = Boolean(router?.isReady);

    if (!isReady) {
        return {isReady, isMobileApp: false};
    }

    const displayType = normalizeDisplayTypeQueryParam(
        router.query[MOBILE_APP_DISPLAY_TYPE_QUERY_PARAM],
    );

    return {isReady, isMobileApp: displayType === MOBILE_APP_DISPLAY_TYPE_VALUE};
}
